import { Fragment, useContext, useEffect, useRef } from "react";
import SignOutButton from "../../navigation-txp/SignOutButton";
import {
	Icon,
	Image,
	Link,
	NotificationsModalContent,
	Profile,
	Title,
} from "@wdpr/groot-components";
import Schedule from "components/header/navigation/section/schedule/Schedule";
import MoreParkHours from "components/header/navigation/section/schedule/MoreParkHours";
import { Context } from "Context";
import { validateItemAffiliations } from "utils/affiliations";
import appConfig from "config.json";
import { buildHref } from "utils/link";
import { isRenderableDescription } from "utils/isRenderableDescription";
import {
	WdprCardXlarge,
	WdprCardXlargeContent,
	WdprIcon,
	WdprMedia,
	WdprNavItemMedium,
	WdprNavItemSmall,
	WdprSecondaryNavSection,
} from "components/vista";
import MyPlansSignInPrompt from "components/shared/MyPlansSignInPrompt";
import AnalyticsWrapper from "components/shared/AnalyticsWrapper";
import {
	txpMotionClasses,
	txpMotionDelayMs,
	isProfileSection,
} from "config-txp";

import { getNotificationAnalyticsLinkId } from "utils";

const { targetOneId } = appConfig;
const mainClass = "syndicated-link-groups";
const mainListClass = `${mainClass}__list`;
const mainListItemClass = `${mainListClass}__item`;
const mainLinkClass = `${mainClass}__link`;
const mainTitleClass = `${mainClass}__title`;
const moreParkHoursId = "moreParkHours";
const authenticationOptionsId = "authenticationOptions";

const defaultAffiliationId = "default";

const section = ({ children }) => (
	<WdprSecondaryNavSection>{children}</WdprSecondaryNavSection>
);

const LinkGroupTxp = ({
	id,
	text,
	webLinkList,
	descriptions,
	modules,
	profile,
	link,
	groupType,
	isDecorative = false,
	duplicateSectionLinkOnMobile = false,
	showInMobile = false,
	reduceLayout = false,
	handleFullClose,
	title,
	showTitleInMobile = false,
	animationInitialDelay = 0,
	columns,
	column1,
	column2,
	isHeaderHovered,
	hoveredSection,
	compactDelays,
}) => {
	const isClosing = isHeaderHovered && !hoveredSection;

	const getAnimationDelay = ({
		index = 0,
		isTitle = false,
		noImage = false,
	}) => {
		if (isClosing) {
			return `${txpMotionDelayMs.none}ms`;
		}
		if (compactDelays) {
			if (!user) {
				return noImage || isTitle
					? `${txpMotionDelayMs.big}ms`
					: `${txpMotionDelayMs.tiny}ms`;
			}
			return noImage || isTitle
				? `${txpMotionDelayMs.compactNoImage}ms`
				: `${txpMotionDelayMs.compactDefault}ms`;
		}
		if (isTitle) {
			return `${txpMotionDelayMs.none}ms`;
		}
		if (!isMobileView && (secondary || tertiary)) {
			return `${txpMotionDelayMs.tiny}ms`;
		}
		if (!secondary && !tertiary && noImage) {
			return `${txpMotionDelayMs.big}ms`;
		}
		return `${
			animationInitialDelay + index * txpMotionDelayMs.betweenAnimations
		}ms`;
	};

	const toggleAnimationClasses = (modifier = false) => {
		if (isMobileView) {
			return `${txpMotionClasses.motionFadeIn} ${txpMotionClasses.motionPositionNavEnterLeft}`;
		}
		if (isHeaderHovered && !hoveredSection) {
			return `${txpMotionClasses.motionFadeOut}`;
		}

		if (
			(secondary || tertiary || modifier) &&
			isHeaderHovered &&
			hoveredSection
		) {
			return txpMotionClasses.motionFadeIn;
		}

		if (isHeaderHovered && hoveredSection && !(secondary || tertiary)) {
			return `${txpMotionClasses.motionFadeIn} ${txpMotionClasses.motionPositionEnterGentleDown}`;
		}
		return "";
	};

	const totalModules =
		(column1?.modules?.length || 0) + (column2?.modules?.length || 0);
	const columnsClass =
		column1 || column2
			? ` ${mainClass}--columns-2x${totalModules >= 5 ? "3" : "2"}`
			: "";

	const {
		profileData: { user = null, handleLogout, affiliations, isLoading } = {},
		headerData: { chrome, extraOptions, interstitial },
		config: { brand, isTxp, shouldInvertItems, store, basePath, showSections },
		showUserStatus,
		chat: { chatEnabled, launchChat },
		cdnAssetsUrl,
		isMobileView,
		setModal,
		handleUpdateNotifications,
	} = useContext(Context);

	const rootRef = useRef(null);

	const titleClass = `${mainTitleClass}${
		id ? ` ${mainTitleClass}--${id}` : ""
	}`;

	const tertiaryListClass = "tertiary-list";
	const primaryListClass = "primary-list__item";
	const secondaryListClasses = `secondary-list ${
		isProfileSection(id) ? "secondary-list--my-plans" : ""
	}`.trim();

	const { secondary, tertiary } = {
		secondary: groupType === "secondary",
		tertiary: groupType === "tertiary",
	};

	const processHref = (href) => buildHref(href, store, basePath, false);

	const handleShowNotifications = () => {
		setModal({
			content: (
				<NotificationsModalContent
					action={() => setModal(false)}
					updateNotificationsAction={handleUpdateNotifications}
					messages={user?.messages}
				/>
			),
		});
	};

	const renderTertiaryItem = ({
		image,
		id,
		title,
		link,
		gradient = "accent-6",
		visibleCount = 1,
	}) => {
		const mediaLayouts = {
			landscape: { aspect: "landscape", aspectRatio: "3:2", isMobile: true },
			portrait: { aspect: "portrait", aspectRatio: "2:3", isMobile: false },
		};
		const mediaLayout =
			isMobileView || visibleCount > 1
				? mediaLayouts.landscape
				: mediaLayouts.portrait;

		if (!image || !link?.href) return null;

		return (
			<AnalyticsWrapper
				analyticsLinkId={link?.analyticsLinkId}
				link={link}
			>
				<div
					className={`${tertiaryListClass}__item `}
					key={id || link?.analyticsLinkId}
					data-mode="small"
				>
					<WdprCardXlarge
						content-orientation="bottom"
						gradient-color={gradient}
						media-layout={mediaLayout.aspectRatio}
						variant="actionable"
						href={processHref(link?.href)}
						target={link?.target}
						a11yLabel={title || link?.title}
						usesCompactTypography
					>
						<WdprMedia
							slot="media"
							src={image?.url}
							aspect={mediaLayout.aspect}
						></WdprMedia>
						<WdprCardXlargeContent
							slot="content"
							heading-level="h3"
							variant="primary"
							primaryHeadline={title || link?.title || ""}
							headlineSize="xsmall"
						/>
					</WdprCardXlarge>
				</div>
			</AnalyticsWrapper>
		);
	};

	const hasMedia = ({ icon, image, mobileImage }) => {
		const imageToUse = isMobileView ? mobileImage || image : image;
		return imageToUse || icon;
	};

	const renderDescription = (
		{ vistaIcon, icon, link, title, image, mobileImage, gradient, id },
		isPromo = false,
		role = null,
		visibleCount = 1
	) => {
		const imageToUse = isMobileView ? mobileImage || image : image;
		const media = hasMedia({ icon, image, mobileImage });
		const navLabel = title || link?.title;

		if (!title && !link?.title && gradient !== "none") {
			return null;
		}
		return !tertiary ? (
			media ? (
				<AnalyticsWrapper
					analyticsLinkId={link?.analyticsLinkId}
					link={link}
				>
					<WdprNavItemMedium
						label={navLabel}
						mediaType={icon ? "icon" : "image"}
						mediaSize="large"
						itemId={id}
						a11yLabel={navLabel}
						inverse={shouldInvertItems}
						href={processHref(link?.href)}
						target={link?.target}
						className={primaryListClass}
					>
						{imageToUse ? (
							<WdprMedia
								slot="media"
								src={imageToUse.url}
								alt={imageToUse.title}
								fade="true"
								aspect="square"
								shape="flat"
								object-fit="fill"
								aria-hidden={isPromo ? true : null}
								role={role && !isPromo ? role : null}
							></WdprMedia>
						) : icon ? (
							<WdprIcon
								icon={vistaIcon || icon}
								background="square"
								variant="info"
								size="large"
								slot="media"
								decorative
							/>
						) : null}
					</WdprNavItemMedium>
				</AnalyticsWrapper>
			) : isMobileView ? (
				<AnalyticsWrapper
					analyticsLinkId={link?.analyticsLinkId}
					link={link}
				>
					<WdprNavItemSmall
						label={navLabel}
						href={processHref(link?.href)}
						target={link?.target}
						inverse={shouldInvertItems}
						allowRichTextLabel={true}
					/>
				</AnalyticsWrapper>
			) : (
				<AnalyticsWrapper
					analyticsLinkId={link?.analyticsLinkId}
					link={link}
				>
					<WdprNavItemMedium
						label={navLabel}
						mediaSize="large"
						itemId={id}
						inverse={shouldInvertItems}
						href={processHref(link?.href)}
						target={link?.target}
					></WdprNavItemMedium>
				</AnalyticsWrapper>
			)
		) : (
			renderTertiaryItem({
				image: imageToUse,
				id,
				isPromo,
				title,
				role,
				link,
				visibleCount,
				gradient,
			})
		);
	};

	const renderLinkGroup = (
		descriptions,
		isDescription = false,
		isPromo = false,
		isWebLinkList = false,
		linkGroupId = "",
		ariaLabelledById = null
	) => {
		// Skip rendering the whole group when no description would render anything
		// (no href, image, title, or inline image), so empty groups
		// (e.g. only authenticationOptions after target-audience filtering)
		// don't take up visual space.
		const hasRenderableDescription = descriptions.some((description) =>
			isRenderableDescription(description, { isDescription, moreParkHoursId })
		);
		if (!hasRenderableDescription) {
			return null;
		}
		const newAffiliations = chrome?.newAffiliations === "true";
		const hasParkHoursIcon = descriptions.find(
			(description) =>
				(description.id === moreParkHoursId || description.parkHour) &&
				description.icon
		);
		const descriptionsIteratorLimit = descriptions.length - 1;
		let schedulesCounter = 0;
		// TODO: Remove this logic once all brands have the new affiliations content
		let affiliationsToUse = [defaultAffiliationId];
		if (!newAffiliations && (isPromo || tertiary)) {
			if (
				affiliations.length &&
				descriptions.filter(({ id }) => affiliations.includes(id)).length
			) {
				affiliationsToUse = [...affiliations];
			}
		}

		let groupClass = mainListClass;
		if (id) groupClass += ` ${mainListClass}--${id}`;
		if (isPromo && isDescription) groupClass += ` ${mainListClass}--promo`;
		if (linkGroupId) groupClass += ` ${mainListClass}--${linkGroupId}`;
		if (isWebLinkList) groupClass += ` ${mainListClass}--link-list`;

		const isAffiliationFiltered = isPromo || tertiary;
		const visibleCount = isAffiliationFiltered
			? descriptions.filter((description) => {
					if (newAffiliations) {
						return validateItemAffiliations(description, affiliations);
					}
					return affiliationsToUse.includes(description.id);
			  }).length
			: descriptions.length;

		const isVisibleByAffiliation = (description) => {
			if (!isAffiliationFiltered) return true;
			if (newAffiliations) {
				return validateItemAffiliations(description, affiliations);
			}
			return affiliationsToUse.includes(description.id);
		};

		const linkContent = (link, description) => {
			const showNotificationBadge =
				showUserStatus &&
				getNotificationAnalyticsLinkId(brand).includes(link?.analyticsLinkId) &&
				user?.messages?.itemCount > 0;
			const showNotificationsModal =
				showNotificationBadge && interstitial?.interstitialNotificationsModal;
			return (
				<Fragment>
					{isDescription && !secondary ? (
						renderDescription(description, isPromo, "link", visibleCount)
					) : (
						<AnalyticsWrapper
							analyticsLinkId={link?.analyticsLinkId}
							link={link}
						>
							<WdprNavItemSmall
								label={description?.title}
								href={showNotificationsModal ? null : processHref(link?.href)}
								target={link?.target}
								inverse={shouldInvertItems}
								className={`${secondaryListClasses}${
									description?.richHtml ? " rich-html" : ""
								}`}
								{...(showNotificationsModal && {
									onClick: handleShowNotifications,
								})}
								{...(showNotificationBadge && {
									showNotificationBadge,
									notificationNumber: user?.messages?.itemCount,
								})}
								allowRichTextLabel={true}
							/>
						</AnalyticsWrapper>
					)}
					{isPromo && <Icon icon="next" brand={brand} cdnUrl={cdnAssetsUrl} />}
				</Fragment>
			);
		};
		const sectionLink = descriptions.find((description) => {
			return description.id && description.id.includes("Link");
		});
		const renderDescriptions = descriptions.map((description, index) => {
			if (!isVisibleByAffiliation(description)) {
				return null;
			}

			if (description.id === authenticationOptionsId) {
				groupClass += ` ${mainListClass}--${authenticationOptionsId}`;
			}

			let listItemClass = mainListItemClass;
			if (description.id)
				listItemClass += ` ${mainListItemClass}--${description.id}`;
			if (isDecorative) listItemClass += ` ${mainListItemClass}--decorative`;
			if (reduceLayout && index !== 0)
				listItemClass += ` ${mainListItemClass}--reduceLayout`;

			if (description.image)
				listItemClass += ` ${mainListItemClass}--with-image`;

			const link = isDescription ? description.link : description;
			const target = description?.target;

			let linkClass = mainLinkClass;
			if (link) {
				if (id) linkClass += ` ${mainLinkClass}--${id}`;
				if (brand) linkClass += ` ${mainLinkClass}--${brand}`;
				if (
					description.id === moreParkHoursId ||
					(isDescription &&
						!description.title &&
						!description.image &&
						!description.icon)
				)
					linkClass += ` ${mainLinkClass}--empty`;
				if (
					["entertainmentsSchedule", "parkCalendar"].includes(description.id) &&
					hasParkHoursIcon
				)
					linkClass += ` ${mainLinkClass}--with-icons`;
				if (secondary && index === descriptionsIteratorLimit) {
					linkClass += ` ${mainLinkClass}--last-item`;
				}
			}

			if (description.id === moreParkHoursId) {
				return (
					<MoreParkHours
						description={description}
						linkClass={linkClass}
						listItemClass={listItemClass}
						name={id}
						titleClass={titleClass}
						key={index}
					/>
				);
			}

			if (description.parkHour) {
				schedulesCounter++;
				return (
					<Schedule
						description={description}
						link={link}
						listItemClass={listItemClass}
						linkClass={linkClass}
						key={index}
					/>
				);
			}

			if (link?.href === "#chat") {
				return renderChatInteractionLink(
					index,
					listItemClass,
					link,
					description,
					linkClass,
					linkContent
				);
			}

			if (description.displayInlineImage) {
				const { image, subtitle, link, title } = description;
				return (
					<li
						className={`${mainListItemClass} ${mainListItemClass}--inline-image`}
						key={index}
					>
						<Title title={title} name={id} />
						<Image image={image} />
						<Link {...link}>
							<Title subTitle={subtitle} name={description.id} />
						</Link>
					</li>
				);
			}

			const primaryListClasses = `${primaryListClass}${
				!description?.image ? "--no-image" : ""
			}`;

			if (!description.image && !description.title) {
				return null;
			}

			return (
				<li
					key={index}
					className={`${mainListItemClass} ${toggleAnimationClasses(
						!tertiary ? !(description.image || description.icon) : false
					)} ${!tertiary && !secondary ? primaryListClasses : ""}`}
					style={{
						animationDelay: getAnimationDelay({
							index,
							noImage: !(description?.image || description?.icon),
						}),
					}}
				>
					{link || showInMobile ? (
						link?.target === targetOneId ? (
							<Profile
								oneId
								target={link.target}
								renderComponent={(handleClick) => (
									<Link
										{...link}
										handleClick={handleClick}
										className={linkClass}
									>
										{linkContent(link, description)}
									</Link>
								)}
							/>
						) : index === 0 && showInMobile ? (
							isMobileView && reduceLayout ? (
								<Link {...sectionLink.link} className={linkClass}>
									{linkContent(sectionLink.link, description)}
								</Link>
							) : (
								renderDescription(description, false, null, visibleCount)
							)
						) : secondary ? (
							<AnalyticsWrapper
								analyticsLinkId={link?.analyticsLinkId}
								link={link}
							>
								{store && descriptions.length === 1 && index === 0 ? (
									<WdprNavItemMedium
										label={description.title}
										mediaSize="large"
										itemId={id}
										inverse={shouldInvertItems}
										href={processHref(link?.href)}
										{...(target && {
											target,
										})}
									></WdprNavItemMedium>
								) : (
									<WdprNavItemSmall
										label={description.title}
										inverse={shouldInvertItems}
										href={processHref(link?.href)}
										target={link?.target}
										className={`${secondaryListClasses}${
											description?.richHtml ? " rich-html" : ""
										}`}
										allowRichTextLabel={true}
									/>
								)}
							</AnalyticsWrapper>
						) : (
							<>{linkContent(link, description)}</>
						)
					) : (
						renderDescription(description, false, null, visibleCount)
					)}
				</li>
			);
		});

		if (schedulesCounter > 3) groupClass += ` ${mainListClass}-—column-layout`;

		const shouldShowTitle =
			title && (!isMobileView || tertiary || showTitleInMobile);

		return secondary ? (
			<ul className={groupClass}>
				{title && (
					<span
						slot="label"
						className={`${mainListClass}__title ${toggleAnimationClasses(
							true
						)}`}
						style={{
							animationDelay: getAnimationDelay({ isTitle: true }),
						}}
					>
						{title}
					</span>
				)}
				{renderDescriptions}
			</ul>
		) : (
			<WdprSecondaryNavSection
				role="none"
				key={shouldShowTitle ? "with-title" : "no-title"}
				className={`${mainListClass}${
					tertiary ? `--${tertiaryListClass}` : ""
				}`}
			>
				{shouldShowTitle && (
					<span
						slot="label"
						className={`${mainListClass}__title ${toggleAnimationClasses(
							true
						)}`}
						style={{
							animationDelay: getAnimationDelay({ isTitle: true }),
						}}
					>
						{title}
					</span>
				)}
				<ul className={tertiary ? `${tertiaryListClass}` : ""}>
					{renderDescriptions}
				</ul>
			</WdprSecondaryNavSection>
		);
	};

	const renderTitleAsLink = () => {
		if (duplicateSectionLinkOnMobile && isMobileView) {
			const webLinkList = [
				{ analyticsLinkId: "", href: link?.href, title: link?.title },
			];
			return (
				webLinkList &&
				renderLinkGroup(webLinkList, false, false, false, id, null)
			);
		}
	};

	const renderModules = (modules = []) => {
		return modules?.map(
			({ webLinkList, title, descriptions, isPromo = false, id }, index) => {
				const ariaLabelledById = id ? `navigation-${id}` : null;
				const ModuleWrapper = isPromo || !title ? Fragment : section;
				return (
					<ModuleWrapper key={index}>
						{title && (
							<span
								slot="label"
								className={`${mainListClass}__title ${toggleAnimationClasses(
									true
								)}`}
								style={{
									animationDelay: getAnimationDelay({ isTitle: true }),
								}}
							>
								{title}
							</span>
						)}
						{webLinkList &&
							renderLinkGroup(
								webLinkList,
								false,
								false,
								false,
								id,
								ariaLabelledById
							)}
						{descriptions && renderLinkGroup(descriptions, true, isPromo)}
					</ModuleWrapper>
				);
			}
		);
	};

	const renderChatInteractionLink = (index, listItemClass, link) => {
		if (chatEnabled && launchChat) {
			const handleChatClick = async () => {
				try {
					await launchChat(handleFullClose);
				} catch (error) {
					console.error(error);
				}
			};
			return (
				<li
					key={index}
					className={`${mainListItemClass} ${toggleAnimationClasses(
						!tertiary ? true : false
					)}`}
					style={{
						animationDelay: getAnimationDelay({
							index,
							noImage: true,
						}),
					}}
				>
					<AnalyticsWrapper analyticsLinkId={link?.analyticsLinkId}>
						<WdprNavItemSmall
							label={link?.title}
							inverse={shouldInvertItems}
							onClick={handleChatClick}
							className={secondaryListClasses}
						/>
					</AnalyticsWrapper>
				</li>
			);
		} else {
			return null;
		}
	};

	// TEMP PATCH (until Vista exposes a `part` on nav-wdpr-nav-item-small's label
	// span): secondary links whose title contains rich HTML (e.g. <i>...</i>) clip
	// the italic text on the left. The label span lives in the component's shadow
	// DOM with no ::part hook, so we apply the offset inline. Remove once Vista
	// exposes part="label" or fixes this alignment problem. 
	// Vista ticket: https://disneyexperiences.atlassian.net/browse/GEW-2501
	useEffect(() => {
		const root = rootRef.current;
		if (!root) return;

		root
			.querySelectorAll("nav-wdpr-nav-item-small.rich-html")
			.forEach((host) => {
				const labelSpan = host?.shadowRoot?.querySelector("section span");
				if (!labelSpan) return;
				labelSpan.style.position = "relative";
				labelSpan.style.paddingLeft = "2px";
				labelSpan.style.left = "-2px";
			});
	});

	return (
		<div
			ref={rootRef}
			className={`${mainClass} ${mainClass}--${brand}${
				secondary && columns && columns === 2 ? columnsClass : ""
			}`}
		>
			{isProfileSection(id) && extraOptions?.[id] && (
				<>
					{!isLoading && showUserStatus && !user && chrome?.signIn && (
						<MyPlansSignInPrompt
							mainClass={mainClass}
							showDivider={showSections}
							animate={compactDelays && isHeaderHovered && !!hoveredSection}
						/>
					)}
					{showSections && (
						<>
							{descriptions && renderLinkGroup(descriptions, true)}
							{modules && renderModules(modules)}
						</>
					)}
				</>
			)}
			{!isLoading &&
				showUserStatus &&
				profile &&
				!isMobileView &&
				(isTxp && user ? (
					<SignOutButton
						handleLogout={handleLogout}
						className={secondaryListClasses}
						animationClasses={toggleAnimationClasses(true)}
						animationDelay={getAnimationDelay({ noImage: true })}
					/>
				) : (
					<Profile userStatusLinks detailed />
				))}
			{text && (
				<div
					className={`${mainClass}__text ${
						id ? `${mainClass}__text--${id}` : ""
					}`}
					dangerouslySetInnerHTML={{ __html: text }}
				/>
			)}
			{webLinkList && renderLinkGroup(webLinkList, false, false, true)}
			{renderTitleAsLink()}
			{!isProfileSection(id) && (
				<>
					{column1 || column2 ? (
						<>
							{column1?.modules && renderModules(column1.modules)}
							{column2?.modules && renderModules(column2.modules)}
						</>
					) : (
						modules && renderModules(modules)
					)}
					{descriptions && renderLinkGroup(descriptions, true)}
				</>
			)}
		</div>
	);
};

export default LinkGroupTxp;
